import { MonitorDown } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { DialogProps } from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { congressMetaQueryKey } from "../api/get-congress";
import { useMigrateCongress } from "../api/migrate-congress";

export function MigrationModal(props: DialogProps) {
  const { t } = useTranslation();
  const { congress } = useCongress();
  const queryClient = useQueryClient();

  const {
    mutateAsync: migrateCongress,
    isPending,
    isError,
    error,
    isSuccess,
    data: migrationResult,
    reset,
  } = useMigrateCongress(congress.id, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: congressMetaQueryKey(congress.id),
      });
    },
  });

  return (
    <Dialog
      {...props}
      onOpenChange={(open) => {
        if (!open) {
          reset();
        }

        props.onOpenChange?.(open);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" type="button" variant="outline">
          <MonitorDown className="mr-1 size-3.5" />
          {t("migrationModal.trigger")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isSuccess ? t("migrationModal.successTitle") : t("migrationModal.title")}
          </DialogTitle>
        </DialogHeader>
        {!isSuccess && (
          <>
            <p>{t("migrationModal.intro")}</p>
            <ul className="list-inside list-disc text-sm">
              <li>{t("migrationModal.abstract")}</li>
              <li>{t("migrationModal.section")}</li>
              <li>{t("migrationModal.break")}</li>
            </ul>

            <Button
              type="button"
              disabled={isPending}
              onClick={() => migrateCongress()}
              isLoading={isPending}
            >
              {t("migrationModal.run")}
            </Button>
          </>
        )}

        {isError && (
          <div>
            <Alert variant="destructive">
              <ScrollArea className="flex max-h-72 flex-col">
                <AlertDescription className="space-y-2">
                  {Array.isArray(error.body.message) ? (
                    error.body.message.map((item) => (
                      <p key={item.path}>
                        <span className="block text-sm">
                          {t("migrationModal.value")}: {item.value}
                        </span>
                        <span className="block text-sm">
                          {t("migrationModal.path")}: {item.path}
                        </span>
                        <span className="block text-sm">
                          {t("migrationModal.message")}: {item.message}
                        </span>
                      </p>
                    ))
                  ) : (
                    <p>{error.body.message}</p>
                  )}
                </AlertDescription>
              </ScrollArea>
            </Alert>
          </div>
        )}

        {isSuccess && (
          <>
            <p>{t("migrationModal.doneIn", { seconds: migrationResult.duration / 1000 })}</p>
            <ul>
              <li>{t("migrationModal.resultAbstract")}: {migrationResult.abstracts}</li>
              <li>{t("migrationModal.resultSection")}: {migrationResult.sessions}</li>
              <li>{t("migrationModal.resultBreak")}: {migrationResult.breaks}</li>
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
